/**
 * Global auth context for AgentMeeting.
 *
 * Wraps the app with `<AuthProvider>` and exposes `useAuth()` to any
 * descendant component. On mount, if a token is present in localStorage we
 * call `/api/me` to hydrate the current user; a 401 clears the token. All
 * network errors are swallowed (logged) so the UI never gets stuck on a
 * loading spinner because of a flaky backend.
 *
 * Routing is intentionally not coupled to this module: `useRequireAuth`
 * accepts a `goTo` callback so the caller decides how navigation happens.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { auth, getToken, setToken, HTTPError, type User } from './api';

export type { User };

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  signIn: (token: string, user: User) => void;
  signOut: () => void;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const loadMe = useCallback(async () => {
    const tok = getToken();
    if (!tok) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await auth.me();
      setUser(me);
    } catch (err) {
      if (err instanceof HTTPError && err.status === 401) {
        setToken(null);
        setUser(null);
      } else {
        // Network error or unexpected failure — don't trap the UI in
        // "loading" forever. Surface to the console for debugging.
        // eslint-disable-next-line no-console
        console.warn('auth.me() failed:', err);
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const signIn = useCallback((token: string, u: User) => {
    setToken(token);
    setUser(u);
    setLoading(false);
    // If the user is on an auth page, kick them to /app/meetings. We defer with
    // setTimeout so the navigation happens after React commits the state
    // updates above; doing it synchronously inside the closure was being
    // swallowed by React's batching during form submit.
    if (typeof window !== 'undefined') {
      const h = window.location.hash || '';
      if (h.startsWith('#/signin') || h.startsWith('#/signup')
          || h.startsWith('#/login') || h.startsWith('#/auth')) {
        setTimeout(() => { window.location.href = '/#/app/meetings'; }, 50);
      }
    }
  }, []);

  const signOut = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const refresh = useCallback(async () => {
    await loadMe();
  }, [loadMe]);

  const value: AuthContextValue = {
    user,
    loading,
    signIn,
    signOut,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth() must be used inside <AuthProvider>');
  }
  return ctx;
}

/**
 * Redirect guard: when auth has finished loading and there is no user, calls
 * `goTo(redirectTo)` exactly once. Caller owns the routing primitive — this
 * hook never touches `location` directly.
 */
export function useRequireAuth(
  goTo: (route: string) => void,
  redirectTo: string,
) {
  const { user, loading } = useAuth();
  const fired = useRef(false);

  useEffect(() => {
    if (loading) return;
    if (user) return;
    if (fired.current) return;
    fired.current = true;
    goTo(redirectTo);
  }, [user, loading, goTo, redirectTo]);
}
